/**
 * Money display (docs/06, docs/14 "Money"): mono, amount then currency code, grouping from the
 * locale, negatives in parentheses on finance screens. Input is the wire shape
 * `{ amountMinor: string, currency }` (bigint minor units as a string); never floats.
 */
export type MoneyLike = { amountMinor: string; currency: string };

/** ISO 4217 minor-unit exponents that are not 2. */
const EXPONENTS: Record<string, number> = {
  JPY: 0,
  KRW: 0,
  VND: 0,
  CLP: 0,
  BHD: 3,
  KWD: 3,
  OMR: 3,
  JOD: 3,
  TND: 3,
};

export function currencyExponent(currency: string): number {
  return EXPONENTS[currency.toUpperCase()] ?? 2;
}

export type FormatMoneyOptions = {
  locale?: string;
  /** `(1,240.00)` instead of `-1,240.00` (finance screens). */
  parensNegative?: boolean;
  /** Omit the currency code (tables with a currency column). */
  hideCurrency?: boolean;
};

export function formatMoney(money: MoneyLike, options: FormatMoneyOptions = {}): string {
  const exp = currencyExponent(money.currency);
  let minor = BigInt(money.amountMinor);
  const negative = minor < 0n;
  if (negative) minor = -minor;
  const base = 10n ** BigInt(exp);
  const whole = minor / base;
  const frac = minor % base;
  const grouped = new Intl.NumberFormat(options.locale ?? 'en-US', { maximumFractionDigits: 0 }).format(
    whole,
  );
  const decimal =
    new Intl.NumberFormat(options.locale ?? 'en-US').formatToParts(1.1).find((p) => p.type === 'decimal')
      ?.value ?? '.';
  const amount = exp > 0 ? `${grouped}${decimal}${frac.toString().padStart(exp, '0')}` : grouped;
  const signed = negative ? (options.parensNegative ? `(${amount})` : `-${amount}`) : amount;
  return options.hideCurrency ? signed : `${signed} ${money.currency.toUpperCase()}`;
}
